/**
 * FrameQualityGate — admission control for decoded frame rendering on the
 * JT/T1078 path.
 *
 * Why it exists
 * -------------
 * The TCP-bridged JT/T1078 relay delivers raw NAL units to the browser. If
 * any P-frame in a GOP is lost (relay back-pressure dropped it, or a
 * decoder transient produced a corrupted reference frame), every subsequent
 * P-frame in that GOP references state the decoder no longer has. The
 * decoder happily emits corrupted pictures (green/pink blocks, ghosting)
 * until the next IDR — typically 1-5 seconds away on the firmware's GOP
 * cadence.
 *
 * The gate sits in front of the decoder and tracks an "anchor" state:
 *   - ANCHORED: the decoder has decoded a valid keyframe + any subsequent
 *     frames have arrived contiguously. Frames pass through.
 *   - ORPHANED: the gate has detected a likely anchor-loss event (PTS gap,
 *     decoder error). Subsequent non-keyframe frames are DROPPED until the
 *     next keyframe re-anchors the decoder. The host emits a
 *     `frame_quality: 'recovering'` event so the UI can show a spinner.
 *
 * Detection signals
 * -----------------
 * 1. PTS gap > N × expected frame interval — a frame likely went missing
 *    between the encoder and the decoder, so any P-frame after the gap
 *    references state the decoder doesn't have.
 * 2. Decoder error (WebCodecs `error` callback, or wasm decoder throw) —
 *    the bitstream itself is corrupted and the decoder rejected it.
 *
 * Recovery
 * --------
 * On entry to ORPHANED the host MAY call `runtime.requestCachedKeyframe()`
 * to ask the device to re-push its cached IDR from RAM (cheap, no encoder
 * disturbance — see firmware KEYFRAME_CACHE.md). If no such method is
 * available or the device replies `cache_empty`, the gate simply waits
 * for the next natural IDR.
 */

/** State surfaced to the host (and onward to SdkEvent). */
export type FrameQualityState = 'ok' | 'recovering';

/** Why the gate entered 'recovering'. */
export type FrameQualityReason = 'pts_gap' | 'decoder_error' | 'cache_empty';

export interface FrameQualityChange {
  state: FrameQualityState;
  reason?: FrameQualityReason;
  /** Frames dropped since the last 'ok' event. Reset to 0 on each 'ok'. */
  droppedFrames: number;
}

export interface FrameQualityGateOptions {
  /** Expected frame interval in ms (e.g. 67 for 15 fps). Updated on VIDEO_INIT. */
  expectedFrameIntervalMs?: number;
  /** Multiplier on expected interval. PTS gaps larger than this × expectedFrameIntervalMs flip to ORPHANED. Default 3. */
  ptsGapThresholdMultiplier?: number;
  /** Min ms between consecutive `recovering` state-change emissions while still recovering. Default 0 (every transition). */
  minRecoveringEmitGapMs?: number;
  /**
   * Max ms to keep dropping P-frames while waiting for a re-anchor keyframe.
   * If no keyframe arrives within this window (device didn't honour the
   * recovery `request`, or `cache_empty`), the gate gives up freezing and
   * admits P-frames again — accepting brief visible artifact in exchange for
   * never stalling longer than this. A later real IDR re-anchors cleanly.
   * Set to 0 to disable the fallback (drop until keyframe, original behaviour).
   * Default 800.
   */
  recoveringFallbackMs?: number;
  /** Injectable clock (ms) for tests. Default Date.now. */
  nowFn?: () => number;
  /**
   * If you want to expose a hook that fires whenever a frame is admitted (for stats),
   * pass it here. Optional — default is no-op.
   */
  onFrameAdmitted?: () => void;
}

export class FrameQualityGate {
  private state: FrameQualityState = 'ok';
  private lastPtsUs: number | null = null;
  private droppedFrames = 0;
  private expectedFrameIntervalMs: number;
  private readonly ptsGapThresholdMultiplier: number;
  private readonly configuredFallbackMs: number;
  private readonly nowFn: () => number;
  /** Wall-clock ms when the gate last entered 'recovering' (null while 'ok'). */
  private recoveringSinceMs: number | null = null;
  private readonly listeners = new Set<(change: FrameQualityChange) => void>();
  private readonly onFrameAdmitted: () => void;

  // PLAN-live-field-r2-round2-player.md §3 B/C: `expectedFrameIntervalMs` is
  // fed from the encoder's fps in VIDEO_INIT, which is 0 in production — the
  // static 67ms/3x (=200ms) threshold this class shipped with misses a
  // single-frame hole at 25 fps (one dropped 40ms step = an 80ms gap, under
  // 200ms). Learn the real interval from the stream instead: a running
  // median of the last `intervalWindowSize` NORMAL forward PTS steps (not
  // gaps we already flagged), threshold 1.9x that median once we have some.
  private static readonly intervalWindowSize = 64;
  private readonly intervalWindow: number[] = [];
  private learnedIntervalMs: number | null = null;
  // §3 C: recoveringFallbackMs=800 (fixed) re-admits corrupted deltas after
  // 0.8s, which on a 4s-GOP stream brings corruption back for up to 3.2s of
  // that GOP. Learn the IDR interval instead (max keyframe spacing seen,
  // capped 5s) and fall back at 1.25x that (hard cap 5s) — long enough that
  // the fallback essentially never fires before the next real keyframe on a
  // healthy link, short enough to bound a genuinely stuck recovery.
  private lastKeyframePtsUs: number | null = null;
  private learnedIdrIntervalMs: number | null = null;

  constructor(opts: FrameQualityGateOptions = {}) {
    this.expectedFrameIntervalMs = opts.expectedFrameIntervalMs ?? 67; // 15 fps default, bootstrap only
    this.ptsGapThresholdMultiplier = opts.ptsGapThresholdMultiplier ?? 3;
    this.configuredFallbackMs = opts.recoveringFallbackMs ?? 800;
    this.nowFn = opts.nowFn ?? (() => Date.now());
    this.onFrameAdmitted = opts.onFrameAdmitted ?? (() => {});
  }

  /** Threshold for "this forward PTS step is a hole", ms. */
  private currentThresholdMs(): number {
    return this.learnedIntervalMs !== null
      ? this.learnedIntervalMs * 1.9
      : this.expectedFrameIntervalMs * this.ptsGapThresholdMultiplier;
  }

  /** How long to keep dropping deltas before giving up and admitting anyway, ms. 0 = configured-disabled. */
  private currentFallbackMs(): number {
    if (this.configuredFallbackMs === 0) return 0;
    if (this.learnedIdrIntervalMs !== null) return Math.min(this.learnedIdrIntervalMs * 1.25, 5000);
    return this.configuredFallbackMs;
  }

  private recordInterval(gapMs: number): void {
    if (gapMs <= 0 || gapMs > 1000) return; // ignore backward steps and gaps we already scored as holes
    this.intervalWindow.push(gapMs);
    if (this.intervalWindow.length > FrameQualityGate.intervalWindowSize) this.intervalWindow.shift();
    const sorted = [...this.intervalWindow].sort((a, b) => a - b);
    this.learnedIntervalMs = sorted[Math.floor(sorted.length / 2)];
  }

  private recordKeyframe(ptsUs: number): void {
    if (this.lastKeyframePtsUs !== null) {
      const idrGapMs = (ptsUs - this.lastKeyframePtsUs) / 1000;
      if (idrGapMs > 0 && idrGapMs < 20_000) {
        const capped = Math.min(idrGapMs, 5000);
        this.learnedIdrIntervalMs = this.learnedIdrIntervalMs === null ? capped : Math.max(this.learnedIdrIntervalMs, capped);
      }
    }
    this.lastKeyframePtsUs = ptsUs;
  }

  /** Subscribe to gate-state changes. Returns an unsubscribe function. */
  onChange(listener: (change: FrameQualityChange) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Update the expected frame interval. Call from `_handleVideoInit` when the
   * encoder reports an fps value.
   */
  setExpectedFrameIntervalMs(intervalMs: number): void {
    if (intervalMs > 0 && intervalMs < 1000) {
      this.expectedFrameIntervalMs = intervalMs;
    }
  }

  /**
   * Pre-decode admission check. Returns true if the frame should be decoded,
   * false if it should be dropped.
   */
  admit(ptsUs: number, isKeyframe: boolean): boolean {
    // Keyframe always re-anchors the gate.
    if (isKeyframe) {
      if (this.state === 'recovering') {
        this.flipTo('ok');
      }
      this.recordKeyframe(ptsUs);
      this.lastPtsUs = ptsUs;
      this.onFrameAdmitted();
      return true;
    }

    // Already orphaned — drop until a keyframe arrives, UNLESS the re-anchor
    // keyframe is overdue. Past the fallback the device clearly did not
    // honour the recovery request in time; freezing longer hurts the viewer
    // more than a brief artifact. Admit the P-frame, re-baseline, and flip
    // back to 'ok' (clears the UI spinner). A later real IDR re-anchors
    // cleanly; until then the picture may show transient corruption.
    if (this.state === 'recovering') {
      const fallbackMs = this.currentFallbackMs();
      if (
        fallbackMs > 0 &&
        this.recoveringSinceMs !== null &&
        this.nowFn() - this.recoveringSinceMs >= fallbackMs
      ) {
        this.lastPtsUs = ptsUs;
        this.flipTo('ok');
        this.onFrameAdmitted();
        return true;
      }
      this.droppedFrames++;
      this.totalDroppedFrames++;
      return false;
    }

    // PTS-gap detection. Skip on the very first frame (no baseline).
    if (this.lastPtsUs !== null) {
      const gapMs = (ptsUs - this.lastPtsUs) / 1000;
      const thresholdMs = this.currentThresholdMs();

      // Forward gap beyond threshold: frames genuinely went missing between the
      // encoder and the decoder, so subsequent P-frames reference state the
      // decoder no longer has → orphan until the next keyframe.
      if (gapMs > thresholdMs) {
        this.droppedFrames++;
        this.totalDroppedFrames++;
        this.flipTo('recovering', 'pts_gap');
        return false;
      }

      // Backward PTS within one threshold window = benign reordering. The relay
      // reassembly / jitter buffer occasionally delivers a frame slightly out of
      // order (observed: a steady -1-frame dip at each GOP boundary). The decoder
      // handles small reorders fine; treating every dip as anchor-loss tripped a
      // false "recovering" every GOP. Admit the frame WITHOUT regressing the
      // baseline (keep the peak PTS) so a later real forward gap still measures
      // correctly. Only a LARGE backward jump (stream reset / seek) is a true
      // discontinuity worth orphaning on.
      if (gapMs < 0) {
        if (-gapMs > thresholdMs) {
          this.droppedFrames++;
          this.totalDroppedFrames++;
          this.flipTo('recovering', 'pts_gap');
          return false;
        }
        this.onFrameAdmitted();
        return true;
      }

      this.recordInterval(gapMs);
    }

    this.lastPtsUs = ptsUs;
    this.onFrameAdmitted();
    return true;
  }

  /**
   * Signal that the decoder rejected a frame (post-decode error). Orphans the
   * gate so subsequent P-frames are dropped until the next keyframe.
   */
  notifyDecoderError(): void {
    if (this.state === 'recovering') return;
    this.flipTo('recovering', 'decoder_error');
  }

  /**
   * Signal that a request_cached_keyframe came back with `cache_empty`. The
   * gate stays in recovering, but the reason is updated so the UI can
   * differentiate "we asked for help but device has nothing" from "we just
   * dropped a frame".
   */
  notifyCacheEmpty(): void {
    if (this.state !== 'recovering') return;
    this.emit({
      state: 'recovering',
      reason: 'cache_empty',
      droppedFrames: this.droppedFrames,
    });
  }

  /** Current state — for stats/debug. */
  get currentState(): FrameQualityState {
    return this.state;
  }

  // PLAN-live-field-r2-round2-player.md §3 F: cumulative counters for
  // getStats(), reset alongside everything else on `reset()` — i.e. "since
  // this stream started", matching the per-phase/per-VIDEO_INIT lifetime the
  // bench measures against.
  private totalDroppedFrames = 0;
  private recoveringMsTotal = 0;
  private holesDetected = 0;

  /** Round-2 stats (PLAN §3 F): gate state + cumulative drop/recovery counters. */
  getGateStats(): { gateState: FrameQualityState; gateDroppedFrames: number; gateRecoveringMsTotal: number; holesDetected: number } {
    return {
      gateState: this.state,
      gateDroppedFrames: this.totalDroppedFrames,
      gateRecoveringMsTotal: this.recoveringMsTotal,
      holesDetected: this.holesDetected,
    };
  }

  /** Reset on detach or on VIDEO_INIT (resolution change). */
  reset(): void {
    const wasRecovering = this.state === 'recovering';
    this.state = 'ok';
    this.lastPtsUs = null;
    this.droppedFrames = 0;
    this.recoveringSinceMs = null;
    this.intervalWindow.length = 0;
    this.learnedIntervalMs = null;
    this.lastKeyframePtsUs = null;
    this.learnedIdrIntervalMs = null;
    this.totalDroppedFrames = 0;
    this.recoveringMsTotal = 0;
    this.holesDetected = 0;
    if (wasRecovering) {
      this.emit({ state: 'ok', droppedFrames: 0 });
    }
  }

  private flipTo(newState: FrameQualityState, reason?: FrameQualityReason): void {
    if (this.state === newState) return;
    this.state = newState;
    if (newState === 'ok') {
      if (this.recoveringSinceMs !== null) this.recoveringMsTotal += this.nowFn() - this.recoveringSinceMs;
      this.recoveringSinceMs = null;
      const dropped = this.droppedFrames;
      this.droppedFrames = 0;
      this.emit({ state: 'ok', droppedFrames: dropped });
    } else {
      this.recoveringSinceMs = this.nowFn();
      if (reason === 'pts_gap') this.holesDetected += 1;
      this.emit({
        state: 'recovering',
        reason,
        droppedFrames: this.droppedFrames,
      });
    }
  }

  private emit(change: FrameQualityChange): void {
    for (const listener of this.listeners) {
      try {
        listener(change);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[FrameQualityGate] listener threw:', err);
      }
    }
  }
}
